import { cn } from "@/lib/utils";
import { Type } from "lucide-react";

export const STRUCTURE_KEYS = [
  "titleLength",
  "hyphenation",
  "leadPhrase",
  "bulletDensity",
] as const;
export type StructureKey = (typeof STRUCTURE_KEYS)[number];

export type StructureTreatments = Record<StructureKey, string | null>;

export const emptyStructureTreatments = (): StructureTreatments =>
  STRUCTURE_KEYS.reduce<StructureTreatments>((acc, k) => {
    acc[k] = null;
    return acc;
  }, {} as StructureTreatments);

interface StructureOption {
  key: StructureKey;
  label: string;
  description: string;
  options: { value: string; label: string; hint: string }[];
}

const STRUCTURE_OPTIONS: StructureOption[] = [
  {
    key: "titleLength",
    label: "Title length",
    description: "How long the new title should run.",
    options: [
      { value: "short", label: "Shorter", hint: "≤ ~120 chars, tighter" },
      { value: "match", label: "Match current", hint: "Keep similar length" },
      { value: "long", label: "Longer", hint: "Use the full ~200 chars" },
    ],
  },
  {
    key: "hyphenation",
    label: "Hyphenation & punctuation",
    description: "How separators chunk the title.",
    options: [
      { value: "minimal", label: "Minimal", hint: "Commas, almost no hyphens" },
      { value: "moderate", label: "Moderate", hint: "A few hyphenated phrases" },
      { value: "heavy", label: "Heavy", hint: "Multiple hyphen-delimited blocks" },
    ],
  },
  {
    key: "leadPhrase",
    label: "Lead phrase",
    description: "What comes first after the product name.",
    options: [
      { value: "benefit", label: "Benefit-led", hint: '"Visibly fades…"' },
      { value: "audience", label: "Audience-led", hint: '"For sensitive skin…"' },
      { value: "ingredient", label: "Ingredient-led", hint: '"With 15% Vitamin C…"' },
      { value: "usecase", label: "Use-case-led", hint: '"For your morning routine…"' },
    ],
  },
  {
    key: "bulletDensity",
    label: "Bullet density",
    description: "How packed each bullet point reads.",
    options: [
      { value: "scannable", label: "Scannable", hint: "Short, one idea per bullet" },
      { value: "balanced", label: "Balanced", hint: "Headline + supporting clause" },
      { value: "dense", label: "Dense", hint: "Multiple claims per bullet" },
    ],
  },
];

interface StructurePanelProps {
  value: StructureTreatments;
  onChange: (key: StructureKey, value: string | null) => void;
  disabled?: boolean;
}

export function StructurePanel({ value, onChange, disabled }: StructurePanelProps) {
  return (
    <div className={cn("space-y-3", disabled && "pointer-events-none opacity-50")}>
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <Type className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <p className="text-sm font-semibold text-foreground">Copy structure</p>
        </div>
        <p className="text-xs text-muted-foreground leading-snug">
          Pure formatting levers — title length, hyphenation, lead phrase, bullet density. Independent of the positioning fingerprint above. Leave blank to keep the current treatment.
        </p>
      </div>

      <div className="rounded-lg border border-border bg-card divide-y divide-border">
        {STRUCTURE_OPTIONS.map((row) => (
          <div key={row.key} className="px-3 py-3 space-y-2">
            <div className="flex items-baseline justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-semibold text-foreground">{row.label}</p>
                <p className="text-[11px] text-muted-foreground leading-snug">{row.description}</p>
              </div>
              {value[row.key] && (
                <button
                  type="button"
                  onClick={() => onChange(row.key, null)}
                  className="text-[11px] text-muted-foreground hover:text-foreground shrink-0"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {row.options.map((opt) => {
                const active = value[row.key] === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => onChange(row.key, active ? null : opt.value)}
                    title={opt.hint}
                    className={cn(
                      "rounded-full border px-2.5 py-0.5 text-xs transition-colors",
                      active
                        ? "border-primary bg-primary/15 text-foreground font-medium"
                        : "border-border bg-card text-muted-foreground hover:bg-primary/10 hover:text-foreground",
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
